import { LibraryKpiUnit } from '@digital-twins/shared-types'

import { intlLocaleForUi } from '@/i18n'
import { SocialTopicDelta, socialTopicDelta } from '@/pages/KnowledgeHubPage/social/charts/socialTopicMetrics'
import { formatTopicStat } from '@/pages/KnowledgeHubPage/social/SocialStatRow'

/**
 * A callout's value and its change vs the previous window as a
 * `SocialTopicDelta`: previous = value - delta; up, down or flat; `percent`
 * is |delta| over the previous window in whole percent, null when the
 * previous window is below `MIN_DELTA_PERCENT_BASE` (`socialTopicDelta`).
 * Null when either input is null, absent or not finite.
 */
export function toKpiDelta(
  value: number | null,
  delta: number | null | undefined,
): SocialTopicDelta | null {
  if (value == null || delta == null) {
    return null
  }
  if (!Number.isFinite(value) || !Number.isFinite(delta)) {
    return null
  }
  return socialTopicDelta(delta, value - delta)
}

/** A whole number, never NaN and never a negative zero. */
function wholeNumber(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.round(value) || 0
}

/**
 * A callout value in its unit: `count` through `formatTopicStat` (grouped,
 * compact from 10,000), `percent` as whole percent ("94%"), `net` signed
 * ("+71", "-12", "0"). The value may be a tweened fraction, so it is rounded;
 * never NaN. `SocialStat` prints the n/a for a null value, so this only sees
 * numbers.
 */
export function formatKpiValue(value: number, unit: LibraryKpiUnit, uiLanguage: string): string {
  const whole = wholeNumber(value)
  const locale = intlLocaleForUi(uiLanguage)
  if (unit === 'percent') {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
      whole / 100,
    )
  }
  if (unit === 'net') {
    return new Intl.NumberFormat(locale, { signDisplay: 'exceptZero' }).format(whole)
  }
  return formatTopicStat(whole, uiLanguage)
}
